import MarketingImage from '@/components/landing/MarketingImage';
import React from 'react';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Check,
  Flame,
  Heart,
  Moon,
  MoreHorizontal,
  Sun
} from 'lucide-react';
const biomarkerImage = '/images/marketing/landing-practice-shift-biomarker.webp';
const smartIntakeImage = '/images/marketing/landing-practice-shift-smart-intake.webp';
const trackImage = '/images/marketing/landing-practice-shift-track.svg';
import styles from './PracticeShiftSection.module.css';

const biomarkers = [
  ['Glucose, Fasting', '82 mg/dL', 'teal'],
  ['Vitamin D, 25-OH', '45 ng/mL', 'teal'],
  ['LDL Cholesterol', '112 mg/dL', 'gold']
];

const meals = [
  ['Breakfast', '450 kcal', Flame],
  ['Lunch', '650 kcal', Sun],
  ['Dinner', '550 kcal', Moon]
];

const PracticeShiftSection = ({
  variant = 'full',
}: {
  /** `sync` keeps only the “Everything in sync” card (used by /start). */
  variant?: 'full' | 'sync';
}) => {
  const syncOnly = variant === 'sync';

  return (
    <section id="how-we-work" className={styles.section}>
      <div className={styles.inner}>
        {!syncOnly && (
          <header className={styles.header}>
            <h2>Spend less time chasing information. More time practicing.</h2>
            <p>This is where that shift happens.</p>
          </header>
        )}

        <div className={syncOnly ? styles.syncOnlyGrid : styles.grid}>
          {!syncOnly && (
            <>
          <article className={styles.biomarkerCard}>
            <p className={styles.eyebrow}>Biomarker intelligence</p>
            <h3>See what matters. Instantly.</h3>
            <p>
              Turn lab reports into structured, trackable clinical insights without manual
              interpretation.
            </p>
            <MarketingImage loading="lazy" src={biomarkerImage} alt="" className={styles.biomarkerImage} aria-hidden />
            <span>Review faster. Handle more cases with clarity.</span>
          </article>

          <article className={styles.intakeCard}>
            <div className={styles.intakeCopy}>
              <p className={styles.eyebrow}>Smart intake</p>
              <h3>Structured before the first session.</h3>
              <p>
                Customisable questionnaires collect complete, usable client data before you even
                begin.
              </p>
              <span>Walk into every session prepared.</span>
            </div>
            <MarketingImage loading="lazy" src={smartIntakeImage} alt="" className={styles.smartIntakeImage} aria-hidden />
            <div className={`${styles.intakeChip} ${styles.lifestyleChip}`}>
              <Activity aria-hidden="true" />
              <span>Lifestyle</span>
            </div>
            <div className={`${styles.intakeChip} ${styles.symptomsChip}`}>
              <span className={styles.chipIconDot}>!</span>
              <span>Symptoms</span>
            </div>
            <div className={`${styles.intakeChip} ${styles.dietHistoryChip}`}>
              <Heart aria-hidden="true" />
              <span>Diet history</span>
            </div>
          </article>

          <article className={styles.templateCard}>
            <h3>Build once. Adapt endlessly.</h3>
            <p>Templates and reusable logic eliminate repetitive tasks.</p>
            <MealTemplate />
            <span>Reduce time per client.</span>
          </article>

          <article className={styles.trackingCard}>
            <h3>Track without switching tools.</h3>
            <p>Logs, and updates all in one place.</p>
            <MarketingImage loading="lazy" src={trackImage} alt="" className={styles.trackImage} aria-hidden />
            <span>Stay on top of every client.</span>
          </article>
            </>
          )}

          <article className={styles.connectedCard}>
            <div className={styles.connectedCopy}>
              <p className={styles.eyebrow}>Scaling your practice</p>
              <h3>Everything in sync. Effortlessly.</h3>
              <p>
                From intake to insights to plans and follow-ups, every part of your workflow
                connects. Less admin, more time for actual practice.
              </p>
              <div className={styles.metricRow}>
                {/* Final values are plain HTML; rendering never depends on a counter or observer. */}
                <span>
                  <ArrowDown aria-hidden="true" />
                  70% Less Planning Time
                </span>
                <span>
                  <ArrowUp aria-hidden="true" />
                  3× Client Capacity
                </span>
                <span>
                  <Check aria-hidden="true" />
                  0 Missed Follow-Ups
                </span>
              </div>
            </div>
            <div className={styles.clientChip}>
              <span />
              <div>
                <strong>Sarah Jenkins</strong>
                <small>Active Client</small>
              </div>
            </div>
            <div className={styles.connectedTemplate}>
              <MealTemplate compact />
            </div>
            <div className={styles.connectedBiomarker}>
              <BiomarkerPanel compact />
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

function BiomarkerPanel({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${styles.panel} ${compact ? styles.compactPanel : ''}`}>
      <div className={styles.panelHeader}>
        <strong>Comprehensive Panel</strong>
        <span>Updated Today</span>
      </div>
      {biomarkers.map(([name, value, tone]) => (
        <div className={styles.bioRow} key={name}>
          <i className={styles[tone]} />
          <span>{name}</span>
          <b>{value}</b>
        </div>
      ))}
      <div className={styles.barChart}>
        {Array.from({ length: 8 }).map((_, index) => (
          <span key={index} style={{ height: `${28 + index * 4 + (index % 2) * 8}%` }} />
        ))}
      </div>
    </div>
  );
}

function MealTemplate({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`${styles.mealTemplate} ${compact ? styles.compactTemplate : ''}`}>
      <div className={styles.templateHeader}>
        <strong>Daily Template</strong>
        <MoreHorizontal aria-hidden="true" />
      </div>
      {meals.map(([name, kcal, Icon]) => (
        <div className={styles.mealRow} key={name as string}>
          <span>
            <Icon aria-hidden="true" />
          </span>
          <b>{name as string}</b>
          <small>{kcal as string}</small>
          <MoreHorizontal aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

export default PracticeShiftSection;
